import { AlertCircle, Check, CheckCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DeliveryStatus } from '@/Domain/api/Conversation/dto/Message.dto';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/Domain/components/Shared/Popover/Popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';

type Props = {
  status: DeliveryStatus;
  errorMessage?: string | null;
};

const DISCORD_FAILURE_TRANSLATIONS = [
  {
    legacyReason: 'Discord returned status 403',
    key: 'discord_reply_forbidden',
  },
  {
    legacyReason: 'Discord rate limit did not clear',
    key: 'discord_reply_rate_limited',
  },
  {
    legacyReason: 'Discord conversation has no authenticated reply route',
    key: 'discord_reply_route_unverified',
  },
] as const;

export const DeliveryStatusIndicator = ({ status, errorMessage }: Props) => {
  const { t } = useTranslation('inbox_v2');

  switch (status) {
    case 'sent':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid="delivery-status-sent" className="inline-flex items-center">
              <Check className="h-3.5 w-3.5 text-foreground-subtle" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('whatsapp_status_sent')}</TooltipContent>
        </Tooltip>
      );
    case 'delivered':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid="delivery-status-delivered" className="inline-flex items-center">
              <CheckCheck className="h-3.5 w-3.5 text-foreground-subtle" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('whatsapp_status_delivered')}</TooltipContent>
        </Tooltip>
      );
    case 'read':
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span data-testid="delivery-status-read" className="inline-flex items-center">
              <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="bottom">{t('whatsapp_status_read')}</TooltipContent>
        </Tooltip>
      );
    case 'failed': {
      const discordFailure = DISCORD_FAILURE_TRANSLATIONS.find(
        ({ legacyReason, key }) =>
          errorMessage === legacyReason || errorMessage === t(key, { lng: 'en' }),
      );
      const reason =
        errorMessage === t('messenger_reply_refused', { lng: 'en' })
          ? t('messenger_reply_refused')
          : errorMessage === t('whatsapp_account_locked', { lng: 'en' })
            ? t('whatsapp_account_locked')
            : discordFailure
              ? t(discordFailure.key)
              : errorMessage || t('whatsapp_status_failed');
      return (
        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  data-testid="delivery-status-failed"
                  aria-label={t('whatsapp_status_failed')}
                  className="inline-flex cursor-pointer items-center rounded-full focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25"
                >
                  <AlertCircle className="h-3.5 w-3.5 text-destructive" />
                </button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">{reason}</TooltipContent>
          </Tooltip>
          <PopoverContent
            side="bottom"
            collisionPadding={8}
            data-testid="delivery-status-failed-reason"
            aria-label={t('whatsapp_status_failed')}
            className="max-w-xs px-3 py-2 text-sm"
          >
            {reason}
          </PopoverContent>
        </Popover>
      );
    }
    default:
      return null;
  }
};
